import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { notifyOk, notifyError } from "@/lib/notify";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Field } from "@/components/forms/fields";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { FALTANTE_LABEL } from "@/components/organizaciones/ExpedienteTab";
import { ONBOARDING_LABEL } from "@/pages/Organizaciones";
import { Plus, KeyRound, ShieldAlert, Copy, Check } from "lucide-react";

const ROL_LABEL = {
  institucional_operador: "Operador institucional",
  institucional_admin: "Administrador institucional",
};
const empty = { nombre: "", email: "", role: "institucional_operador", allowed_site_ids: [] };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function UsuariosExternosTab({ oid, sitios, usuarios, onboardingStatus, faltantes, onReload, esInstitucional = true }) {
  // UI-015: en una organización residencial no se le llama "institucional" al usuario externo.
  const sustantivo = esInstitucional ? "usuario institucional" : "usuario con acceso";
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [tocado, setTocado] = useState(false);
  const [pendiente, setPendiente] = useState(null);   // payload de override pendiente
  const [credencial, setCredencial] = useState(null); // one-time
  const [copiado, setCopiado] = useState(false);
  const activos = (sitios || []).filter((s) => s.estado !== "Inactivo");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleSite = (id) =>
    setF((p) => ({ ...p, allowed_site_ids: p.allowed_site_ids.includes(id)
      ? p.allowed_site_ids.filter((x) => x !== id) : [...p.allowed_site_ids, id] }));

  const emailErr = (tocado || f.email) && !EMAIL_RE.test(f.email.trim())
    ? "Correo inválido. Revisa el formato (nombre@dominio.com)." : "";
  const nombreErr = tocado && !f.nombre.trim() ? "El nombre es obligatorio." : "";
  const sitiosErr = tocado && f.allowed_site_ids.length === 0 ? "Selecciona al menos un sitio." : "";
  const puedeCrear = !!f.nombre.trim() && !emailErr && !!f.role && f.allowed_site_ids.length > 0;

  const crear = async (override = false) => {
    setTocado(true);
    if (!puedeCrear) return;
    setSaving(true);
    try {
      const r = await api.post("/users/externos", {
        ...f, organization_id: oid, override_expediente_pendiente: override,
      });
      setPendiente(null);
      setOpen(false);
      setF(empty);
      setTocado(false);
      setCopiado(false);
      setCredencial(r.data);
      onReload();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.codigo === "expediente_pendiente") {
        setPendiente({ onboarding_status: d.onboarding_status, faltantes: d.faltantes || [] });
      } else {
        notifyError(formatApiErrorDetail(d));
      }
    } finally {
      setSaving(false);
    }
  };

  const regenerar = async (u) => {
    try {
      const r = await api.post(`/users/${u.id}/credencial-temporal`);
      setCopiado(false);
      setCredencial({ ...r.data, email: u.email });
    } catch (e) {
      notifyError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const copiar = async () => {
    const valor = credencial?.credencial_temporal || "";
    try {
      await navigator.clipboard.writeText(valor);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch (e) {
      notifyError("No se pudo copiar automáticamente. Selecciona el texto y cópialo a mano.");
    }
  };

  return (
    <div className="space-y-5" data-testid="tab-usuarios-externos">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <p className="text-xs text-slate-500 max-w-xl">
          Usan el MISMO Central, Radar y ficha de persona, con datos limitados a los sitios
          autorizados. Su operación depende sólo de la seguridad del sistema, no del expediente.
        </p>
        <Button data-testid="add-external-user-button" disabled={activos.length === 0}
          onClick={() => { setF(empty); setTocado(false); setOpen(true); }}
          className="rounded-xl bg-[#01CBE3] hover:bg-[#01B5CB] text-white shrink-0 w-full sm:w-auto">
          <Plus className="h-4 w-4 mr-1.5" /> Crear {sustantivo}
        </Button>
      </div>
      {activos.length === 0 && (
        <p className="text-xs text-amber-700" data-testid="sin-sitios-activos">
          Necesitas al menos un sitio activo en esta organización para crear un {sustantivo}.
        </p>
      )}

      {(usuarios || []).length === 0 ? (
        <p data-testid="org-usuarios-empty" className="text-slate-400 text-sm">Sin {esInstitucional ? "usuarios institucionales" : "usuarios con acceso"}.</p>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100">
          {usuarios.map((u) => (
            <div key={u.id} data-testid={`org-usuario-${u.id}`} className="px-4 sm:px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-[#012472] break-words">{u.nombre || u.email}</p>
                <p className="text-xs text-slate-500 break-words">
                  {u.email} · {ROL_LABEL[u.role] || u.role} · {(u.allowed_site_ids || []).length} sitio(s)
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {u.must_change_password && (
                  <span className="text-[10px] rounded-full bg-amber-50 text-amber-700 px-2 py-0.5">Credencial temporal</span>
                )}
                <span className={`text-xs rounded-full px-2.5 py-1 ${u.estado === "Inactivo" ? "bg-slate-100 text-slate-500" : "bg-emerald-50 text-emerald-700"}`}>
                  {u.estado === "Inactivo" ? "Inactivo" : "Activo"}
                </span>
                <Button variant="outline" size="sm" className="rounded-lg" data-testid={`regenerar-credencial-${u.id}`} onClick={() => regenerar(u)}>
                  <KeyRound className="h-4 w-4 mr-1" /> Nueva credencial
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Crear usuario */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-lg rounded-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-[#012472]">Nuevo usuario institucional</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Verá el mismo Central, Radar y ficha de persona, limitado a los sitios autorizados.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Nombre" error={nombreErr} errorTestId="ext-nombre-error">
              <Input data-testid="ext-nombre-input" className="rounded-xl" value={f.nombre}
                onChange={(e) => set("nombre", e.target.value)} />
            </Field>
            <Field label="Correo electrónico" error={emailErr} errorTestId="ext-email-error">
              <Input data-testid="ext-email-input" type="email" inputMode="email" autoCapitalize="none"
                autoCorrect="off" autoComplete="off" className="rounded-xl" value={f.email}
                onChange={(e) => set("email", e.target.value)} />
            </Field>
            <Field label="Rol">
              <Select value={f.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger className="rounded-xl" data-testid="ext-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ROL_LABEL).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <div>
              <p className="text-sm font-medium text-[#012472] mb-2">Sitios autorizados</p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {activos.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm text-slate-600">
                    <Checkbox checked={f.allowed_site_ids.includes(s.id)} onCheckedChange={() => toggleSite(s.id)}
                      data-testid={`ext-site-${s.id}`} />
                    {s.nombre}
                  </label>
                ))}
              </div>
              {sitiosErr && <p data-testid="ext-sitios-error" className="text-xs text-red-600 mt-1.5">{sitiosErr}</p>}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid="ext-crear-button" disabled={saving || !puedeCrear}
              className="rounded-xl bg-[#01CBE3] hover:bg-[#01B5CB] text-white" onClick={() => crear(false)}>
              Crear y generar credencial
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Override supervisor */}
      <Dialog open={!!pendiente} onOpenChange={(v) => !v && setPendiente(null)}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-md rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="override-modal">
          <DialogHeader>
            <DialogTitle className="text-[#012472] flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-600" /> Expediente administrativo pendiente
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              El acceso institucional funcionará normalmente. Sólo queda registro de que el
              expediente aún no está validado.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Estado del expediente:{" "}
              <strong data-testid="override-estado">
                {ONBOARDING_LABEL[pendiente?.onboarding_status] || "Borrador"}
              </strong>
            </p>
            {(pendiente?.faltantes || []).length > 0 && (
              <>
                <p className="text-xs text-slate-500">Falta por completar:</p>
                <ul className="text-xs text-amber-700 list-disc pl-5" data-testid="override-faltantes">
                  {pendiente.faltantes.map((k) => <li key={k}>{FALTANTE_LABEL[k] || k}</li>)}
                </ul>
              </>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => setPendiente(null)}>Cancelar</Button>
            <Button data-testid="override-confirmar-button" disabled={saving}
              className="rounded-xl bg-amber-600 hover:bg-amber-700 text-white" onClick={() => crear(true)}>
              Autorizar acceso con expediente pendiente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credencial one-time */}
      <Dialog open={!!credencial} onOpenChange={(v) => !v && setCredencial(null)}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-md rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="credencial-modal">
          <DialogHeader>
            <DialogTitle className="text-[#012472]">Credencial temporal</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Se muestra UNA sola vez, expira en 72 horas y debe cambiarse al primer acceso.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600 break-all">{credencial?.email}</p>
            <div className="rounded-xl border border-[#01CBE3]/40 bg-[#01CBE3]/5 px-4 py-3 space-y-3">
              <code data-testid="credencial-temporal-valor"
                onClick={(e) => window.getSelection()?.selectAllChildren(e.currentTarget)}
                className="block text-base font-mono font-semibold tracking-wide text-[#012472] break-all select-all cursor-text">
                {credencial?.credencial_temporal}
              </code>
              <Button variant="outline" size="sm" data-testid="credencial-copiar" onClick={copiar}
                className="rounded-lg w-full">
                {copiado
                  ? (<><Check className="h-4 w-4 mr-1.5" /> Copiada</>)
                  : (<><Copy className="h-4 w-4 mr-1.5" /> Copiar credencial</>)}
              </Button>
              <p className="text-[11px] text-slate-500">
                Si el copiado no funciona, toca el texto para seleccionarlo y cópialo a mano.
              </p>
            </div>
            {credencial?.expediente_pendiente && (
              <p className="text-xs text-amber-700" data-testid="credencial-badge-pendiente">
                Acceso autorizado con expediente pendiente.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button className="rounded-xl bg-[#01CBE3] hover:bg-[#01B5CB] text-white" onClick={() => setCredencial(null)}
              data-testid="credencial-cerrar">Ya la guardé</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
